import type { CalendarEvent } from './types';

// All-day events are stored at midnight UTC and end-exclusive; timed events
// are full ISO timestamps. Shifting by whole days uses UTC for the former and
// local for the latter so wall-clock time stays stable across DST seams.
function shiftIsoByDays(iso: string, days: number, allDay: boolean): string {
  const d = new Date(iso);
  if (allDay) d.setUTCDate(d.getUTCDate() + days);
  else d.setDate(d.getDate() + days);
  return d.toISOString();
}

function shiftIsoByMinutes(iso: string, minutes: number): string {
  return new Date(new Date(iso).getTime() + minutes * 60_000).toISOString();
}

export type EventMovePatch = {
  start_time: string;
  end_time: string;
  all_day: boolean;
};

export function moveByDays(e: CalendarEvent, days: number): EventMovePatch | null {
  if (!e.start_time || !e.end_time) return null;
  return {
    start_time: shiftIsoByDays(e.start_time, days, e.all_day),
    end_time: shiftIsoByDays(e.end_time, days, e.all_day),
    all_day: e.all_day
  };
}

export function moveByMinutes(e: CalendarEvent, minutes: number): EventMovePatch | null {
  if (!e.start_time || !e.end_time) return null;
  return {
    start_time: shiftIsoByMinutes(e.start_time, minutes),
    end_time: shiftIsoByMinutes(e.end_time, minutes),
    all_day: e.all_day
  };
}

// Resize the end of a timed event by a delta in minutes. Enforces a minimum
// duration of 15 minutes so the event doesn't invert or vanish.
export function resizeEndByMinutes(e: CalendarEvent, minutes: number): EventMovePatch | null {
  if (!e.start_time || !e.end_time || e.all_day) return null;
  const start = new Date(e.start_time);
  const end = new Date(new Date(e.end_time).getTime() + minutes * 60_000);
  const minEnd = new Date(start.getTime() + 15 * 60_000);
  const clamped = end < minEnd ? minEnd : end;
  return {
    start_time: e.start_time,
    end_time: clamped.toISOString(),
    all_day: false
  };
}

export const DRAG_THRESHOLD_PX = 4;
